/**
 * PremiumContentToggle
 *
 * Top section of the Monetization step: enables charging the creator's wallet
 * for premium-video coins (only shown when content is not free).
 * Mirrors Flutter `_allowPremiumContentPaymentWidget` from
 * content_monetization_screen.dart.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ToggleSwitch } from '@/presentation/components/common/controls/ToggleSwitch';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { MathUtils } from '@/domain/utils/mathUtils';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';

interface PremiumContentToggleProps {
  enabled: boolean;
  premiumPriceCoins: number;
  onChange: (value: boolean) => void;
}

export const PremiumContentToggle: React.FC<PremiumContentToggleProps> = ({
  enabled,
  premiumPriceCoins,
  onChange,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const description = l10n.publish.premiumPaymentDescription
    .replace('{minutes}', String(MediaUtils.getFreeContentMaxMinutesFromConfigSeconds()))
    .replace('{coinsPerMin}', String(remoteConfigService.premiumContentCoinsPerMinute));

  const chargedText = l10n.publish.coinsWillBeChargedFromWallet.replace(
    '{coins}',
    MathUtils.removeTrailingZero(premiumPriceCoins)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xs }}>
        <AssetIcon
          path="/assets/images/premium_content.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.disabled}
        />
        <span
          style={{
            flex: 1,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
          }}
        >
          {l10n.publish.allowPremiumVideoPayment}
        </span>
        <ToggleSwitch
          value={enabled}
          onChange={onChange}
          qa={{ id: 'monetization-premium-toggle', value: enabled ? 'on' : 'off' }}
        />
      </div>

      <div
        style={{
          fontSize: responsive.font.sm,
          color: theme.text.secondary,
          fontWeight: responsive.fontWeight.medium,
          marginTop: responsive.spacing.sm,
          whiteSpace: 'pre-line',
        }}
      >
        {description}
      </div>

      {enabled && (
        <div
          style={{
            fontSize: responsive.font.sm,
            color: theme.text.primary,
            marginTop: responsive.spacing.lg,
          }}
        >
          {chargedText}
        </div>
      )}
    </div>
  );
};
